import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';
import {
  listMaintenances,
  createMaintenance,
  updateMaintenance,
  deleteMaintenance,
} from '../api/maintenances.api.ts';
import type { MaintenanceListParams } from '@/shared/types/maintenance.ts';
import type { CreateMaintenanceDTO, UpdateMaintenanceDTO } from '../api/maintenances.api.ts';

export const maintenanceKeys = {
  all: ['maintenances'] as const,
  list: (params: MaintenanceListParams) => [...maintenanceKeys.all, 'list', params] as const,
  detail: (id: string) => [...maintenanceKeys.all, 'detail', id] as const,
};

export function useMaintenanceList(params: MaintenanceListParams, enabled = true) {
  return useQuery({
    queryKey: maintenanceKeys.list(params),
    queryFn: () => listMaintenances(params),
    enabled,
  });
}

export function useCreateMaintenance(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMaintenanceDTO) => createMaintenance(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
      toast.success('Manutenção criada com sucesso');
      onSuccess();
    },
    onError: () => {
      toast.error('Erro ao criar manutenção');
    },
  });
}

export function useUpdateMaintenance(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMaintenanceDTO }) =>
      updateMaintenance(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
      toast.success('Manutenção atualizada com sucesso');
      onSuccess();
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(err.response.data?.error ?? 'Manutenção imutável');
        return;
      }
      toast.error('Erro ao atualizar manutenção');
    },
  });
}

export function useDeleteMaintenance(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMaintenance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
      toast.success('Manutenção encerrada com sucesso');
      onSuccess();
    },
    onError: () => {
      toast.error('Erro ao encerrar manutenção');
    },
  });
}
